/**
 * The period an F3 tile falls back to when the board's period has no recorded money yet (on 1 November, "month
 * to date" is a day old and usually empty). A tile then shows the PREVIOUS comparable period, labelled as such,
 * rather than a large empty card.
 *
 * Calendar periods (starting on the 1st) fall back to the same number of WHOLE months just before: month to date
 * in November falls back to all of October, a quarter to the quarter before. Any other range falls back to the
 * same number of days just before it. Days are local calendar days (`YYYY-MM-DD`), as the period picker gives them.
 */
export interface FallbackPeriod {
  from: string
  to: string
  /** `YYYY-MM` when the fallback is exactly one calendar month (it is then named), else null. */
  month: string | null
}

const pad2 = (value: number) => String(value).padStart(2, "0")

const parseDay = (day: string) => {
  const [year, month, date] = day.split("-").map(Number)
  return { year: year ?? 1970, month: month ?? 1, day: date ?? 1 }
}

const formatDay = (at: Date) => `${at.getUTCFullYear()}-${pad2(at.getUTCMonth() + 1)}-${pad2(at.getUTCDate())}`

export function fallbackPeriod(from: string, to: string): FallbackPeriod {
  const start = parseDay(from)
  const end = parseDay(to)

  if (start.day === 1) {
    const span = end.year * 12 + end.month - (start.year * 12 + start.month) + 1
    // Date.UTC normalises month overflow both ways; day 0 of a month is the last day of the one before.
    const prevFrom = new Date(Date.UTC(start.year, start.month - 1 - span, 1))
    const prevTo = new Date(Date.UTC(start.year, start.month - 1, 0))
    return {
      from: formatDay(prevFrom),
      to: formatDay(prevTo),
      month: span === 1 ? `${prevFrom.getUTCFullYear()}-${pad2(prevFrom.getUTCMonth() + 1)}` : null,
    }
  }

  const startUtc = Date.UTC(start.year, start.month - 1, start.day)
  const days = Math.round((Date.UTC(end.year, end.month - 1, end.day) - startUtc) / 86_400_000) + 1
  return {
    from: formatDay(new Date(startUtc - days * 86_400_000)),
    to: formatDay(new Date(startUtc - 86_400_000)),
    month: null,
  }
}

/** A month (`YYYY-MM`) by its full name, capitalised for the start of a subtitle ("Octobre"). */
export function monthLongName(month: string, locale: string): string {
  const name = new Intl.DateTimeFormat(locale, { month: "long" }).format(new Date(`${month}-15T12:00:00`))
  return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1)
}
